import React, {
    Component,
    MouseEvent
} from 'react';
import {connect} from 'react-redux';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import memoize from 'memoize-one';
import {keys} from 'ts-transformer-keys';
import _ from 'lodash';

// TODO: no TS definitions for omniclone available. Consider to implement it or change to other library (lodash?)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import omniclone from 'omniclone';

import {Button} from 'primereact/button';
import {
    Dropdown,
    DropdownProps,
    DropdownChangeEvent
} from 'primereact/dropdown';
import {InputSwitch} from 'primereact/inputswitch';
import {OverlayPanel} from 'primereact/overlaypanel';
import {
    TabPanel,
    TabView,
    TabViewTabChangeEvent
} from 'primereact/tabview';

import {__} from '~/shared/src/utilities/messageCatalog';
import User, {View} from '~/shared/src/model/User';

import {viewSelectorPanelPropsCompare} from '~/frontend/src/components/base/Overview/utilities';

import {StoreState} from '~/frontend/src/store';
import setViewsForModule, {SetViewsForModule} from '~/frontend/src/actions/user/setViewsForModule';
import * as IntercomActions from '~/frontend/src/actions/intercom';
import {
    MsgParams,
    SetMessage,
    unsetAllMessages,
    unsetMessage
} from '~/frontend/src/actions/intercom';


const dropdownDropdownProps = keys<Omit<DropdownProps, 'className'>>();

const Actions = Object.assign( {}, { setViewsForModule }, IntercomActions );


export type ViewOption = {
    key : string;
    type : 'BOOLEAN';
    label : string;
};

export type DefaultProps = {
    /**
     * Options for editing a view.
     */
    viewOptions? : ViewOption[];

    /**
     * The CSS class name(s) to add to the component.
     */
    className? : string;

    /**
     * Should the label for the field been displayed.
     */
    labeled? : boolean;
};
const defaultProps : Required<DefaultProps> = {
    viewOptions: [],
    className  : '',
    labeled    : true
};

export type PanelProps = { [ key : string ] : any };

type Props = DropdownProps & {
    /**
     * The unique key of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    panelKey : string;

    /**
     * The module to filter selectable views for.
     */
    module : string | string[];

    /**
     * The views to be selectable in the dropdown.
     */
    views? : View[];

    /**
     * The currently selected view.
     */
    selectedView? : View;

    /**
     * The current panelProps.
     */
    panelProps? : PanelProps;

    /**
     * The as configured panelProps.
     */
    asConfiguredPanelProps? : PanelProps;

    /**
     * A custom function to process the view to set.
     *
     * @param {PanelProps} panelProps
     */
    onSetView : ( panelProps : PanelProps ) => void,

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;

    style? : React.CSSProperties;

};

type ActionProps = {
    // user actions
    setViewsForModule : SetViewsForModule;

    // intercom actions
    setMessage : SetMessage;
    unsetMessage : typeof unsetMessage;
    unsetAllMessages : typeof unsetAllMessages;
};

export type ComponentProps = DefaultProps & Props;
type AllProps = Required<DefaultProps> & Props & ActionProps;

type State = {
    activeTabIndex? : number;
    focused : boolean;
    views : View[];
    viewToSave : View | undefined;
    viewToDelete : View | undefined;
};

/**
 * Dropdown for ViewSelector selection
 */
class ViewSelectorDropdown extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    overlayPanel? : OverlayPanel | null;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = this.getInitialState( props );

        this.getViewOptions = memoize( this.getViewOptions );

        this.onChange = this.onChange.bind( this );
        this.onFocus = this.onFocus.bind( this );
        this.onBlur = this.onBlur.bind( this );
        this.onShowViewSelectorConfigDialog = this.onShowViewSelectorConfigDialog.bind( this );
        this.onOverlayPanelHide = this.onOverlayPanelHide.bind( this );
        this.onTabChange = this.onTabChange.bind( this );
        this.onViewToSaveChange = this.onViewToSaveChange.bind( this );
        this.onViewToSaveIsDefaultChange = this.onViewToSaveIsDefaultChange.bind( this );
        this.onSaveViewApplyButtonClick = this.onSaveViewApplyButtonClick.bind( this );
        this.onSaveViewCancelButtonClick = this.onSaveViewCancelButtonClick.bind( this );
        this.onViewToDeleteChange = this.onViewToDeleteChange.bind( this );
        this.onDeleteViewApplyButtonClick = this.onDeleteViewApplyButtonClick.bind( this );
        this.onDeleteViewCancelButtonClick = this.onDeleteViewCancelButtonClick.bind( this );
    }

    getInitialState( props : AllProps = this.props ) {
        let views = omniclone( props.views ?? [] ) as View[],
            viewToSave : View | undefined,
            viewToDelete : View | undefined;

        if ( props.selectedView ) {
            ( { viewToSave, views } = this.setViewToSave( props.selectedView, views, false ) );
            ( { viewToDelete, views } = this.setViewToDelete( props.selectedView, views, false ) );
        }

        return {
            focused       : false,
            activeTabIndex: 0,
            views         : views,
            viewToSave    : viewToSave,
            viewToDelete  : viewToDelete
        };
    }

    /**
     * Handler for Dropdown change.
     *
     * @private
     */
    onChange( event : DropdownChangeEvent ) {
        if ( event.value ) {
            const panelProps = Object.assign( {}, event.value.panelProps );
            delete panelProps.default;

            this.props.onSetView( panelProps );
        }
    }

    /**
     * Handler for focus.
     *
     * @private
     */
    onFocus() {
        this.setState( { focused: true } );
    }

    /**
     * Handler for blur.
     *
     * @private
     */
    onBlur() {
        this.setState( { focused: false } );
    }

    /**
     * Handler for showViewSelectorConfigDialog button click.
     *
     * @param event
     *
     * @private
     */
    onShowViewSelectorConfigDialog( event : MouseEvent ) {
        // the state has to be initialized *before* the overlay is shown - doing it in its onShow
        // would run only once the enter animation has finished, at which point the overlay is
        // already usable and anything typed until then would be thrown away again
        this.setState( this.getInitialState() );

        this.overlayPanel?.toggle( event, null );
    }

    /**
     * Handler for OverlayPanel hide.
     *
     * @private
     */
    onOverlayPanelHide() {
        this.setState( this.getInitialState() );
    }

    /**
     * Handler for TabView tabChange.
     *
     * @param event
     *
     * @private
     */
    onTabChange( event : TabViewTabChangeEvent ) {
        this.setState( {
            ...this.getInitialState(),
            activeTabIndex: event.index
        } );
    }

    /**
     * Handler for saveView name Dropdown change.
     *
     * @param event
     *
     * @private
     */
    onViewToSaveChange( event : DropdownChangeEvent ) {
        const value = event.value;

        if ( value ) {
            let viewToSave : View;

            //  only string given (new label entered) -> create a new view
            if ( typeof value === 'string' ) {
                viewToSave = {
                    label: value,
                    new  : true
                } as View;
            }
            else {
                viewToSave = value as View;
            }

            this.setViewToSave( viewToSave );
        }
    }

    /**
     * Handler for saveView isDefault InputToggle change.
     *
     * @param event
     *
     * @private
     */
    onViewToSaveIsDefaultChange( event : DropdownChangeEvent ) {
        const views      = ( [] as View[] ).concat( this.state.views ),
              viewToSave = this.state.viewToSave as View,
              isDefault  = event.value;

        if ( viewToSave ) {
            if ( isDefault ) {
                // reset default within views
                views.forEach( view => {
                    delete view.panelProps.default;
                } );

                // set default
                viewToSave.panelProps.default = true;

                this.setState( {
                    viewToSave,
                    views
                } );
            }
            else {
                // delete default
                delete viewToSave.panelProps.default;

                // set view
                this.setViewToSave( viewToSave );
            }
        }
    }

    /**
     * Handler for saveView viewOption change.
     *
     * @param {ViewOption} viewOption
     * @param event
     *
     * @private
     */
    onViewToSaveViewOptionChange( viewOption : ViewOption, event : DropdownChangeEvent ) {
        const viewToSave = this.state.viewToSave,
              value      = event.value;

        if ( viewToSave ) {
            viewToSave.panelProps[ viewOption.key ] = value;

            this.setViewToSave( viewToSave );
        }
    }

    /**
     * Handler for saveView apply Button click event.
     *
     * @private
     */
    onSaveViewApplyButtonClick() {
        const {
                  panelKey,
                  module,
                  user
              }              = this.props,
              views : View[] = ( [] as View[] ).concat( this.state.views ?? [] ),
              viewToSave     = this.state.viewToSave;

        if ( user ) {
            // remove views not for this module
            for ( let i = views.length - 1; i >= 0; i-- ) {
                if ( views[ i ].key.indexOf( user.buildModuleString( module ) ) !== 0 ) {
                    views.splice( i, 1 );
                }
            }
        }

        // if viewToSave not in views -> add it
        if ( viewToSave ) {
            viewToSave.label = viewToSave.label.trim();
            if ( views.indexOf( viewToSave ) === -1 ) {
                views.push( viewToSave );
            }
        }

        this.overlayPanel?.hide();

        // show save toast
        this.props.setMessage?.(
            panelKey,
            'toast',
            'saveView',
            {
                severity: 'info',
                summary : __( 'YN.base.viewSelector.dialog.saveView.saving.title' ),
                detail  : __( 'YN.base.viewSelector.dialog.saveView.saving.text' )
            }
        );

        const successMsgParams : MsgParams = [
            panelKey,
            'toast',
            'saveViewSuccess',
            {
                severity: 'success',
                summary : __( 'YN.base.viewSelector.dialog.saveView.saveSuccess.title' ),
                detail  : __( 'YN.base.viewSelector.dialog.saveView.saveSuccess.text' )
            }
        ];

        // show success toast
        this.props.setViewsForModule( module, views, successMsgParams );
    }

    /**
     * Handler for saveView cancel Button click event.
     *
     * Just hide the OverlayPanel.
     *
     * @private
     */
    onSaveViewCancelButtonClick() {
        this.overlayPanel?.hide();
    }

    /**
     * Handler for saveView name Dropdown change.
     *
     * @param event
     *
     * @private
     */
    onViewToDeleteChange( event : DropdownChangeEvent ) {
        this.setViewToDelete( event.value as View );
    }

    /**
     * Handler for deleteView apply Button click event.
     *
     * @private
     */
    onDeleteViewApplyButtonClick() {
        const {
                  panelKey,
                  module,
                  user
              }              = this.props,
              views : View[] = ( [] as View[] ).concat( this.state.views ?? [] ),
              viewToDelete   = this.state.viewToDelete;

        if ( user ) {
            // remove views not for this module and view to delete
            for ( let i = views.length - 1; i >= 0; i-- ) {
                if ( views[ i ].key.indexOf( user.buildModuleString( module ) ) !== 0 || views[ i ] === viewToDelete ) {
                    views.splice( i, 1 );
                }
            }
        }

        this.overlayPanel?.hide();

        // show save toast
        this.props.setMessage?.(
            panelKey,
            'toast',
            'deleteView',
            {
                severity: 'info',
                summary : __( 'YN.base.viewSelector.dialog.deleteView.saving.title' ),
                detail  : __( 'YN.base.viewSelector.dialog.deleteView.saving.text' )
            }
        );

        const successMsgParams : MsgParams = [
            panelKey,
            'toast',
            'deleteViewSuccess',
            {
                severity: 'success',
                summary : __( 'YN.base.viewSelector.dialog.deleteView.saveSuccess.title' ),
                detail  : __( 'YN.base.viewSelector.dialog.deleteView.saveSuccess.text' )
            }
        ];

        // show success toast
        this.props.setViewsForModule( module, views, successMsgParams );
    }

    /**
     * Handler for deleteView cancel Button click event.
     *
     * Just hide the OverlayPanel.
     *
     * @private
     */
    onDeleteViewCancelButtonClick() {
        this.overlayPanel?.hide();
    }

    /**
     * Template for view option item.
     *
     * @param {View} view
     *
     * @return {JSX.Element}
     *
     * @private
     */
    viewItemTemplate( view : View ) : JSX.Element {
        let retVal = <></>;

        if ( view ) {
            retVal = <>{view.label}</>;

            if ( view.key === 'asConfigured' ) {
                retVal = <span className="yn-blue">{retVal}</span>;
            }
            else if ( view.new ) {
                retVal = <span className="yn-new">{retVal}</span>;
            }

            if ( view.panelProps.default ) {
                retVal = <strong>{retVal}</strong>;
            }
        }

        return retVal;
    }

    /**
     * Template for selected view
     *
     * @param {View} view
     *
     * @return {JSX.Element}
     *
     * @private
     */
    selectedViewTemplate( view : View ) : JSX.Element {
        let retVal = <>&nbsp;</>;

        if ( view ) {
            retVal = <>{view.label}</>;

            if ( view.new ) {
                retVal = <span className="yn-new">{retVal}</span>;
            }
            if ( view.panelProps.default ) {
                retVal = <strong>{retVal}</strong>;
            }
        }

        return retVal;
    }

    /**
     * Builds and returns the edit view tab panel
     *
     * @return {JSX.Element}
     *
     * @private
     */
    getSaveViewTabPanel() : JSX.Element {
        const views : View[] = ( [] as View[] ).concat( this.state.views ),
              viewToSave     = this.state.viewToSave;

        let selectedView,
            nameDropdownClassName = '';

        if ( viewToSave === null || viewToSave === undefined ) {
            selectedView = this.props.selectedView;
            if ( selectedView ) {
                this.setViewToSave( selectedView );
            }
        }
        else {
            // add new temporary
            if ( views.indexOf( viewToSave ) === -1 ) {
                views.unshift( viewToSave );
            }
        }

        // process name style
        if ( viewToSave ) {
            if ( viewToSave.new ) {
                nameDropdownClassName += ' yn-new';
            }
            if ( viewToSave.panelProps.default ) {
                nameDropdownClassName += ' yn-bold';
            }
        }

        return <TabPanel
            key={`${this.props.panelKey}_saveViewTabPanel`}

            header={__( 'YN.base.viewSelector.dialog.saveView.title' )}
        >
            <div className="field">
                <label
                    htmlFor={`${this.props.panelKey}_viewToSave`}
                    className="block"
                >
                    {__( 'YN.base.viewSelector.dialog.viewName.label' )}
                </label>
                <Dropdown
                    inputId={`${this.props.panelKey}_viewToSave`}
                    value={viewToSave}
                    options={views}
                    onChange={this.onViewToSaveChange}
                    itemTemplate={this.viewItemTemplate}
                    valueTemplate={this.selectedViewTemplate}
                    editable
                    className={nameDropdownClassName}
                    style={{ width: '100%' }}
                />
            </div>

            <label
                className="yn-menu-subtitle-label block"
            >
                {__( 'YN.base.viewSelector.dialog.saveView.options.title' )}
            </label>

            <div className="field">
                <label
                    htmlFor={`${this.props.panelKey}_viewIsDefault`}
                    className="block"
                >
                    {__( 'YN.base.viewSelector.dialog.saveView.default.label' )}
                </label>
                <InputSwitch
                    inputId={`${this.props.panelKey}_viewIsDefault`}
                    checked={viewToSave
                        ? viewToSave.panelProps.default
                        : false}
                    disabled={!viewToSave}
                    onChange={this.onViewToSaveIsDefaultChange}
                />
            </div>

            {this.props.viewOptions ? this.getViewOptions( viewToSave, this.props.viewOptions ) : []}

            <hr className="mt-3 mb-3 yn-ruler"/>

            <div className="flex justify-content-end">
                <Button
                    label={__( 'YN.base.common.save' )}
                    onClick={this.onSaveViewApplyButtonClick}
                    disabled={!viewToSave}
                    className="p-button-outlined mr-3 yn-button-defaultwidth"
                />
                <Button
                    label={__( 'YN.base.common.cancel' )}
                    onClick={this.onSaveViewCancelButtonClick}
                    className="p-button-secondary p-button-outlined yn-button-defaultwidth"
                />
            </div>
        </TabPanel>;
    }

    setViewToSave( viewToSave : View, views : View[] | undefined = undefined, setState : boolean = true ) : { viewToSave : View | undefined; views : View[] } {
        const {
                  module,
                  user
              } = this.props;

        if ( !views ) {
            views = omniclone( this.props.views ?? [] ) as View[];
        }

        let savedCustomizableOptions : { [ key : string ] : any } = {};

        // save customizable options from given view
        if ( viewToSave.panelProps ) {
            savedCustomizableOptions = {
                default: viewToSave.panelProps.default
            };
            this.props.viewOptions?.forEach( option => {
                savedCustomizableOptions[ option.key ] = viewToSave.panelProps[ option.key ];
            } );
        }

        // not a new one -> try to find viewToSave within views and set to new object
        if ( !viewToSave.new ) {
            for ( const view of views ) {
                if ( view.label === viewToSave.label ) {
                    viewToSave = view;
                    break;
                }
            }
        }
        else {
            viewToSave = { ...viewToSave };
        }

        if ( viewToSave && user ) {
            let moduleForKey : string[];

            if ( Array.isArray( module ) ) {
                moduleForKey = ( [] as string[] ).concat( module ).concat( [ viewToSave.label ] )
            }
            else {
                moduleForKey = [ module, viewToSave.label ];
            }

            // set key (since the selected view could be one from another module)
            viewToSave.key = `${user.buildModuleString( moduleForKey )}`;

            // set current panel props and restore saved customizable options
            viewToSave.panelProps = {
                ...this.props.panelProps,
                ...savedCustomizableOptions
            };

            if ( setState ) {
                this.setState( {
                    viewToSave,
                    views
                } );
            }
        }

        return {
            viewToSave,
            views
        }
    }

    /**
     * Builds and returns the fields for the viewOptions.
     *
     * @param {View} viewToSave
     * @param {ViewOption[]} viewOptions
     *
     * @return {JSX.Element[]}
     */
    getViewOptions( viewToSave : View | undefined, viewOptions : ViewOption[] ) : JSX.Element[] {
        const retVal : JSX.Element[] = [];

        viewOptions.forEach( viewOption => {
            const id       = `${this.props.panelKey}_viewOption_${viewOption.key}`,
                  disabled = !viewToSave,
                  path     = viewOption.key.split( '.' );

            let value : PanelProps | null = null,
                field;

            if ( viewToSave ) {
                value = viewToSave.panelProps;

                path.forEach( key => {
                    if ( value !== undefined && value !== null ) {
                        value = value[ key ];
                    }
                } );
            }

            switch ( viewOption.type ) {
                case 'BOOLEAN':
                    field = <InputSwitch
                        inputId={id}
                        checked={!!value}
                        disabled={disabled}
                        onChange={this.onViewToSaveViewOptionChange.bind( this, viewOption )}
                    />;
                    break;
            }

            retVal.push(
                <div key={id} className="field">
                    <label
                        htmlFor={id}
                        className="block"
                    >
                        {viewOption.label}
                    </label>
                    {field}
                </div>
            )
        } );

        return retVal;
    }

    /**
     * Builds and returns the delete view tab panel
     *
     * @return {JSX.Element}
     *
     * @private
     */
    getDeleteViewTabPanel() : JSX.Element {
        const {
                  panelKey,
                  module,
                  user
              }              = this.props,
              views : View[] = ( [] as View[] ).concat( this.state.views ?? [] ),
              viewToDelete   = this.state.viewToDelete;

        let selectedView,
            nameDropdownClassName = '';

        if ( viewToDelete === null || viewToDelete === undefined ) {
            selectedView = this.props.selectedView;
            if ( selectedView ) {
                this.setViewToDelete( selectedView );
            }
        }
        else {
            // add new temporary
            if ( views.indexOf( viewToDelete ) === -1 ) {
                views.unshift( viewToDelete );
            }
        }

        if ( user ) {
            // remove views not for this module
            for ( let i = views.length - 1; i >= 0; i-- ) {
                if ( views[ i ].key.indexOf( user.buildModuleString( module ) ) !== 0 ) {
                    views.splice( i, 1 );
                }
            }
        }

        // process name style
        if ( viewToDelete ) {
            if ( viewToDelete.panelProps.default ) {
                nameDropdownClassName += ' yn-bold';
            }
        }

        return <TabPanel
            key={`${panelKey}_deleteViewTabPanel`}

            header={__( 'YN.base.viewSelector.dialog.deleteView.title' )}
        >
            <div className="field">
                <label
                    htmlFor={`${panelKey}_viewToSave`}
                    className="block"
                >
                    {__( 'YN.base.viewSelector.dialog.viewName.label' )}
                </label>
                <Dropdown
                    inputId={`${panelKey}_viewToDelete`}
                    value={viewToDelete}
                    options={views}
                    onChange={this.onViewToDeleteChange}
                    itemTemplate={this.viewItemTemplate}
                    valueTemplate={this.selectedViewTemplate}
                    className={nameDropdownClassName}
                    style={{ width: '100%' }}
                />
            </div>

            <hr className="mt-3 mb-3 yn-ruler"/>

            <div className="flex justify-content-end">
                <Button
                    label={__( 'YN.base.common.delete' )}
                    onClick={this.onDeleteViewApplyButtonClick}
                    disabled={!viewToDelete}
                    className="mr-3 p-button-outlined yn-button-defaultwidth"
                />
                <Button
                    label={__( 'YN.base.common.cancel' )}
                    onClick={this.onDeleteViewCancelButtonClick}
                    className="p-button-secondary p-button-outlined yn-button-defaultwidth"
                />
            </div>
        </TabPanel>;
    }

    setViewToDelete( viewToDelete : View, views : View[] | undefined = undefined, setState : boolean = true ) : { viewToDelete : View | undefined; views : View[] } {
        if ( !views ) {
            views = omniclone( this.props.views ?? [] ) as View[];
        }

        for ( const view of views ) {
            if ( view.label === viewToDelete.label ) {
                viewToDelete = view;
                break;
            }
        }

        if ( viewToDelete && setState ) {
            this.setState( {
                viewToDelete,
                views
            } );
        }

        return {
            viewToDelete,
            views
        };
    }

    /**
     * REACT lifecycle method
     */
    render() {
        let views = this.props.views;

        const {
                  panelKey,
                  labeled,
                  className,
                  style,
                  selectedView,
                  asConfiguredPanelProps,
                  panelProps
              }                         = this.props as AllProps,
              asConfiguredPanelPropsSet = asConfiguredPanelProps && panelProps && viewSelectorPanelPropsCompare( asConfiguredPanelProps, panelProps ),
              disabled                  = !views?.length && asConfiguredPanelPropsSet,
              placeholder               = views?.length
                  ? __( 'YN.base.viewSelector.field.placeholder.selectView' )
                  : __( 'YN.base.viewSelector.field.placeholder.noView' ),
              dropdownParams            = _.pick<AllProps>( this.props, dropdownDropdownProps );

        let selectedViewToUse = selectedView;

        delete dropdownParams.style;

        // add asConfigured view
        if ( asConfiguredPanelProps ) {
            views = ( [] as View[] ).concat( views ?? [] );
            views.unshift( {
                key       : 'asConfigured',
                label     : __( 'YN.base.viewSelector.view.asConfigured' ),
                panelProps: asConfiguredPanelProps
            } );

            if ( !selectedViewToUse && asConfiguredPanelPropsSet ) {
                selectedViewToUse = views[ 0 ];
            }
        }

        return <div className={className + ' field'}>
            {labeled
                ? <label
                    htmlFor={`${panelKey}_viewSelectorDropdown`}
                    className="block"
                >
                    {__( 'YN.base.viewSelector.field.label' )}
                </label>
                : ''
            }
            <div
                className={`p-inputgroup yn-inputgroup ${this.state.focused
                    ? ' p-focus'
                    : ''}${labeled
                    ? ''
                    : className}`}
                style={style}
            >
                <Dropdown
                    inputId={`${panelKey}_viewSelectorDropdown`}
                    options={views}
                    value={selectedViewToUse}
                    disabled={disabled}
                    onChange={this.onChange}
                    onFocus={this.onFocus}
                    onBlur={this.onBlur}
                    itemTemplate={this.viewItemTemplate}
                    valueTemplate={this.selectedViewTemplate}
                    className="yn-inputgroup"
                    placeholder={placeholder}
                    {...dropdownParams}
                />
                <Button
                    icon="pi pi-cog"
                    onClick={this.onShowViewSelectorConfigDialog}
                    aria-controls={`${panelKey}_viewSelectorConfigDialog`}
                    aria-haspopup
                />
                <OverlayPanel
                    ref={el => this.overlayPanel = el}
                    id={`${panelKey}_viewSelectorConfigDialog`}
                    appendTo={document.body}
                    style={{ width: '25rem' }}
                    dismissable={true}
                    onHide={this.onOverlayPanelHide}
                >
                    <label
                        key={`${panelKey}_viewSelectorConfigDialogHeader`}

                        className="yn-menu-title-label block"
                    >
                        {__( 'YN.base.viewSelector.dialog.title' )}
                    </label>

                    <TabView
                        key={`${panelKey}_viewSelectorConfigDialogTabView`}

                        activeIndex={this.state.activeTabIndex}
                        onTabChange={this.onTabChange}
                    >
                        {this.getSaveViewTabPanel()}
                        {this.getDeleteViewTabPanel()}
                    </TabView>
                </OverlayPanel>
            </div>
        </div>;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState ) : Partial<ComponentProps> {
    const retVal : Partial<ComponentProps> = {
        user: undefined
    };

    // get some session user information
    if ( state?.session.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

// typing of the connect function makes working that the action props are defined but not required when using the component
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default connect<Partial<ComponentProps>, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( ViewSelectorDropdown );
